import { useState } from 'react';
import type { Routine } from '../types';
import { categories, getPractice, practices } from '../lib/catalog';
import {
  deleteRoutine,
  isEdited,
  itemSeconds,
  settingsFor,
  moveItem,
  resetRoutine,
  setRoutineItems,
  setRoutineTitle,
} from '../lib/routines';
import { saveSettings } from '../lib/storage';
import { formatLength } from '../lib/timeline';
import { Icon } from '../ui/Icon';
import { Sheet } from '../ui/parts';
import { Customize } from './Customize';
import styles from './routine.module.css';

interface Props {
  routine: Routine;
  onClose: () => void;
  /** Called after any change so the parent can reload. */
  onChanged: () => void;
  onDeleted: () => void;
}

export const RoutineEditor = ({ routine, onClose, onChanged, onDeleted }: Props) => {
  const [items, setItems] = useState(routine.items);
  const [title, setTitle] = useState(routine.title);
  const [adding, setAdding] = useState(false);
  const [timingFor, setTimingFor] = useState<string | null>(null);
  const [, bump] = useState(0);

  const commit = (next: string[]) => {
    setItems(next);
    setRoutineItems(routine.id, next);
    onChanged();
  };

  const timingPractice = timingFor ? getPractice(timingFor) : null;

  return (
    <>
      <Sheet title="Edit session" onClose={onClose}>
        <label className="visually-hidden" htmlFor="routine-title">Session name</label>
        <input
          id="routine-title"
          className={styles.titleInput}
          value={title}
          maxLength={40}
          onChange={e => setTitle(e.target.value)}
          onBlur={() => {
            setRoutineTitle(routine.id, title);
            onChanged();
          }}
        />

        {items.length === 0 && <p className="eyebrow-soft">Nothing here yet. Add the practices you follow, in order.</p>}

        {items.map((id, i) => {
          const p = getPractice(id);
          if (!p) return null;
          return (
            <div key={`${id}-${i}`} className={styles.editorItem}>
              <button className={styles.editorName} onClick={() => setTimingFor(id)} aria-label={`${p.title}, adjust time`}>
                {p.title}
                <span className={styles.editorSub}>{formatLength(itemSeconds(id, routine))} · tap to adjust time</span>
              </button>
              <button className={styles.tiny} disabled={i === 0} onClick={() => commit(moveItem(items, i, i - 1))} aria-label={`Move ${p.title} up`}>
                ↑
              </button>
              <button className={styles.tiny} disabled={i === items.length - 1} onClick={() => commit(moveItem(items, i, i + 1))} aria-label={`Move ${p.title} down`}>
                ↓
              </button>
              <button className={styles.tiny} onClick={() => commit(items.filter((_, n) => n !== i))} aria-label={`Remove ${p.title}`}>
                <Icon name="close" size={18} />
              </button>
            </div>
          );
        })}

        <div className={styles.footer}>
          <button className="pill-button" style={{ alignSelf: 'stretch', marginTop: 12 }} onClick={() => setAdding(true)}>
            Add a practice
          </button>
          {routine.builtIn && isEdited(routine.id) && (
            <button
              className="text-button"
              onClick={() => {
                resetRoutine(routine.id);
                onChanged();
                onClose();
              }}
            >
              Reset to recommended
            </button>
          )}
          {!routine.builtIn && (
            <button
              className={`text-button ${styles.danger}`}
              onClick={() => {
                deleteRoutine(routine.id);
                onDeleted();
              }}
            >
              Delete this session
            </button>
          )}
        </div>
      </Sheet>

      {adding && (
        <Sheet title="Add a practice" onClose={() => setAdding(false)}>
          {categories.map(cat => {
            const list = practices.filter(p => p.category === cat.id && !p.external);
            if (!list.length) return null;
            return (
              <div key={cat.id}>
                <p className={styles.pickGroup}>{cat.label}</p>
                {list.map(p => (
                  <button key={p.id} className={styles.pick} onClick={() => commit([...items, p.id])}>
                    <Icon name={p.icon} size={22} />
                    <span>{p.title}</span>
                    <span className={styles.pickAdd} aria-hidden="true">+</span>
                  </button>
                ))}
              </div>
            );
          })}
          <button className="pill-button" style={{ width: '100%', marginTop: 16 }} onClick={() => setAdding(false)}>
            Done
          </button>
        </Sheet>
      )}

      {timingPractice && (
        <TimingSheet routine={routine} practiceId={timingPractice.id} onClose={() => { setTimingFor(null); bump(n => n + 1); onChanged(); }} />
      )}
    </>
  );
};

const TimingSheet = ({ routine, practiceId, onClose }: { routine: Routine; practiceId: string; onClose: () => void }) => {
  const practice = getPractice(practiceId)!;
  const [settings, setSettings] = useState(() => settingsFor(routine, practice));
  return (
    <Customize
      practice={practice}
      settings={settings}
      onChange={patch => {
        saveSettings(practice.id, patch);
        setSettings(prev => ({ ...prev, ...patch }));
      }}
      onReset={() => setSettings(settingsFor(routine, practice))}
      onClose={onClose}
    />
  );
};
